import { CaseStudy } from "@components/foundation/CaseStudy";
import { Feature } from "@components/foundation/Feature";
import { FeatureGrid } from "@components/foundation/FeatureGrid";
import { TrustedByCompanies } from "@components/foundation/TrustedByCompanies";
import { WhatItIsSection } from "@components/foundation/WhatItIsSection";
import {
  BrainHeadIcon,
  ClockIcon,
  CodeFileIcon,
  DollarIcon,
  FeedbackIcon,
  FlaskIcon,
  LightningIcon,
  MoneyGraphArrowIncreaseIcon,
  PencilIcon,
  RefreshIcon,
} from "@components/library/Icons";
import { CTASection } from "@components/foundation/CTASection";
import { CodeIcon } from "@heroicons/react/outline";
import { Footer } from "components/foundation/Footer";
import { FoundationHero } from "components/foundation/FoundationHero";
import { Header } from "components/foundation/Header";
import { TagSilhouetteBackground } from "components/foundation/TagSilhouetteBackground";
import { TitleAndMetaTags } from "components/TitleAndMetaTags";
import ProductScreensL from "@public/assets/product/product-screens-l.svg";
import ProductScreensMobile from "@public/assets/product/product-screens-mobile.svg";
import FeedbackDemo from "@public/assets/product/feedback-demo.svg";
import Experiments from "@public/assets/product/experiments.svg";
import FineTuning from "@public/assets/product/finetuning-visual2.svg";
import SDK from "@public/assets/product/sdk.svg";

export default function foundationHome() {
  return (
    <div>
      <TitleAndMetaTags
        title="Humanloop – The platform for GPT-3 applications"
        description="Align and deploy impactful applications of large language models using the Humanloop platform"
        image="/assets/marketing/social/social-gradient.png"
      />
      <main>
        <Header />
        <TagSilhouetteBackground>
          {/* <SimpleHeader /> */}
          <div className="mt-60 ">
            <FoundationHero />
          </div>
          <div className="max-max-w-2xl mx-auto mb-10 mt-80">
            <TrustedByCompanies />
          </div>
          <div className="mx-auto mt-100 flex justify-center">
            <ProductScreensL
              className="hidden w-full max-w-[1193px] object-none md:block" // max-w to manunally prevent scaling above viewBox dimensions
              alt="Humanloop helps you systematically get the most out of next-gen AI"
            />
            <ProductScreensMobile
              className="block w-full md:hidden"
              alt="Humanloop helps you systematically get the most out of next-gen AI"
            />
          </div>
        </TagSilhouetteBackground>
        <div className="-mt-40 mb-40 md:mb-80">
          <WhatItIsSection
            details={[
              {
                Icon: CodeIcon,
                title: "Integrate SDK",
                description:
                  "Simple SDK to log your GPT-3 requests and user feedback",
              },
              {
                Icon: FlaskIcon,
                title: "Experiment",
                description:
                  "Monitor and A/B test different prompts and models to create high performing experiences",
              },
              {
                Icon: LightningIcon,
                title: "Fine-tune",
                description:
                  "Select relevant data and fine-tune new models with the press of a button",
              },
            ]}
          />
        </div>

        <Feature
          name={"Feedback"}
          Icon={FeedbackIcon}
          // title="Human feedback provid"
          title="Drive performance directly from user feedback"
          // Uncover the issues you’re missing from your user feedback
          description="Eye-balling a few examples isn't enough. Collect end-user feedback at scale to unlock actionable insights on how to improve your models."
          Image={FeedbackDemo}
          imageAlt="Best practices for collecting and using user feedback and interactions"
          list={[
            "Adopt best practices for feedback collection",
            "Discover the issues you're missing",
            "Easily log explicit and implicit signals through SDK",
            // "Performance graphs and data exploration tools",
          ]}
        />

        <Feature
          name={"Experiments"}
          Icon={FlaskIcon}
          title="Automatically find the best prompts and parameters"
          description="Easily A/B test models and prompts with the improvement engine built for GPT-3. "
          Image={Experiments}
          imageAlt="A/B test between models to find the best"
          list={[
            "Compare prompts or different models",
            "A/B testing and multi-armed bandit optimization",
            "Find the best models and reduce cost",
          ]}
          // getStarted
        />

        <div className="dark my-60">
          <FeatureGrid
            header="Helps you improve your models"
            features={[
              {
                name: "More accurate",
                description: "Use your data to make better models",
                Icon: MoneyGraphArrowIncreaseIcon,
              },
              {
                name: "Lower latency",
                description: "Up to 100x faster with fine-tuned models",
                Icon: ClockIcon,
              },
              {
                name: "Save money",
                description: "Spend your tokens wisely",
                Icon: DollarIcon,
              },
              {
                name: "Remove repetition",
                description: "Remove repetition",
                Icon: RefreshIcon,
              },
              {
                name: "Prevent 'hallucinations'",
                description: "Fact checking endpoint for your generated text",
                Icon: BrainHeadIcon,
                // citationNeeded: true,
              },
              {
                name: "Customize Tone",
                description: "Tailored to appease your desired tonality",
                Icon: PencilIcon,
              },
            ]}
          />
        </div>

        {/* <div className="dark my-60">
          <FeatureGrid
            header="Improve your model"
            features={[
              {
                name: "More accurate",
                description: "Use your data to make better models",
                Icon: MoneyGraphArrowIncreaseIcon,
              },
              {
                name: "Lower latency",
                description: "Up to 100x faster with fine-tuned models",
                Icon: ClockIcon,
              },
              {
                name: "Save money",
                description: "Spend your tokens wisely",
                Icon: DollarIcon,
              },
            ]}
          />
        </div> */}

        <Feature
          name={"Fine-tuning"}
          Icon={LightningIcon}
          title="Fine-tune with a single click"
          description="Prompts only get your so far. Get higher quality results by fine-tuning on your best data – no coding or data science required."
          Image={FineTuning}
          imageAlt="Easily fine tune models on your best data"
          list={[
            "Faster, cheaper, better models",
            "Model and data management",
            "Competitive advantage from your data",
          ]}
        />
        {/* <div className="my-60">
          <FeatureGrid
            header="Improve your generations"
            features={[
              {
                name: "Remove repetition",
                description: "Remove repetition",
                Icon: RefreshIcon,
              },
              {
                name: "Prevent 'hallucinations'",
                description: "Fact checking endpoint for your generated text",
                Icon: BrainHeadIcon,
                citationNeeded: true,
              },
              {
                name: "Customize Tone",
                description: "Tailored to appease your desired tonality",
                Icon: PencilIcon,
              },
            ]}
          />
        </div> */}

        <Feature
          name={"SDK"}
          Icon={CodeFileIcon}
          title="One API – multiple models & providers"
          description="Integration as easy as changing one line of code. Experiment with other language model providers or custom models without touching code."
          Image={SDK}
          imageAlt="Best practices for collecting and using user feedback and interactions"
          list={[
            "Access all leading LLM providers",
            "Compare cost and quality across models",
            "Hosted open source models available",
          ]}
        />

        {/* <OverviewSection /> */}
        <div className="dark my-60">
          <CaseStudy />
        </div>

        <TagSilhouetteBackground>
          <CTASection />
        </TagSilhouetteBackground>
        <Footer />
      </main>
    </div>
  );
}
